import { XMLParser } from 'fast-xml-parser';

function isUltravioletItem(item) {
    const value = typeof item === 'string' ? item : item?.name || item?.description || '';
    if (!value) return false;
    return /ультрафиолет|уф\b|ultraviolet|uv\b/i.test(value);
}

function getHeaterPower(item) {
    if (!item) return 0;
    const name = item.name || '';
    const description = item.description || '';
    const text = `${name} ${description}`.toLowerCase();
    const toNumber = (v) => parseFloat(String(v).replace(',', '.'));

    const kw = text.match(/(\d+(?:[.,]\d+)?)\s*(?:квт|kw)/i);
    if (kw) return toNumber(kw[1]);

    const w = text.match(/(\d+(?:[.,]\d+)?)\s*(?:ватт|вт|w)\b/i);
    if (w) return parseFloat((toNumber(w[1]) / 1000).toFixed(3));

    const hp = text.match(/(\d+(?:[.,]\d+)?)\s*(?:hp|л\.?с\.?)/i);
    if (hp) return parseFloat((toNumber(hp[1]) * 0.7355).toFixed(3));

    return 0;
}

function extractRecommendedPoolVolume(name = '', description = '', offer = null) {
    const text = `${name} ${description}`.toLowerCase();

    const range = text.match(/(\d+(?:[.,]\d+)?)\s*[-–]\s*(\d+(?:[.,]\d+)?)\s*(?:м3|м³|m3|m³)/i);
    if (range) {
        const min = parseFloat(range[1].replace(',', '.'));
        const max = parseFloat(range[2].replace(',', '.'));
        if (Number.isFinite(min) && Number.isFinite(max) && min > 0 && max > 0) return { min, max };
    }

    if (offer?.param) {
        const params = Array.isArray(offer.param) ? offer.param : [offer.param];
        const joined = params.map(p => {
            const n = p['@_name'] || '';
            const v = p['#text'] || '';
            return `${n}: ${v}`;
        }).join(' • ').toLowerCase();

        const r2 = joined.match(/(\d+(?:[.,]\d+)?)\s*[-–]\s*(\d+(?:[.,]\d+)?)\s*(?:м3|м³|m3|m³)/i);
        if (r2) {
            const min = parseFloat(r2[1].replace(',', '.'));
            const max = parseFloat(r2[2].replace(',', '.'));
            if (Number.isFinite(min) && Number.isFinite(max) && min > 0 && max > 0) return { min, max };
        }
    }

    return null;
}

function isHeatPumpItem(source) {
    const value = typeof source === 'string'
        ? source
        : source?.name || source?.description || '';
    if (!value) return false;
    if (/теплообменник/i.test(value)) return false;
    return /теплов(ой|ые)\s*насос|heat\s*pump|heatpump/i.test(value);
}

/**
 * Парсит XML текст в нормализованный список объектов оборудования.
 * @param {string} xmlText 
 * @returns {Array} 
 */
export function parseXMLData(xmlText) {
    const parser = new XMLParser({
        ignoreAttributes: false,
        attributeNamePrefix: '@_',
        parseTagValue: true,
        trimValues: true,
        textNodeName: '#text',
    });

    try {
        const yml = parser.parse(xmlText);
        return transformXMLData(yml);
    } catch (e) {
        console.error("XML Parse Error", e);
        return [];
    }
}

function transformXMLData(xmlData) {
    const offers = xmlData?.yml_catalog?.shop?.offers?.offer;
    const list = Array.isArray(offers) ? offers : offers ? [offers] : [];
    if (!list.length) return [];
    return list.map((offer, idx) => normalizeOffer(offer, idx)).filter(Boolean);
}

function normalizeOffer(offer, index) {
    const id = offer['@_id'] || offer.vendorCode || safeStr(offer.url) || `item_${index}`;
    const name = safeStr(offer.name) || safeStr(offer.model) || safeStr(offer.vendor);
    if (!name) return null;

    const vendor = safeStr(offer.vendor);
    const url = safeStr(offer.url);
    const price = toNumber(offer.price);
    const pictures = toArray(offer.picture).map(String).filter(Boolean);
    const picture = pictures[0] || '';
    const description = mergeDescription(offer);
    const available = offer['@_available'] !== 'false';
    const currency = offer.currencyId || 'KZT';
    const isRegularPump = /насос.*префильтр|префильтр.*насос|циркуляцион|glong/i.test(name) || /префильтр|glong/i.test(description);
    const category = determineCategory(name, description, offer, isRegularPump);

    // Извлечение параметров через делегирование сущностям
    let flowM3h = 0;
    let headM = 0;
    let powerkW = 0;
    let voltage = extractVoltage(name, description, offer);
    let recommendedPoolVolume = null;

    // Специфичная логика для категорий
    if (category === 'pump') {
        flowM3h = getPumpCapacityFromName(name, description, offer);
        headM = extractHead(name, description, offer);
    } else if (category === 'filters') {
        const fFlow = parseFilterFlow({ name, raw: offer });
        if (!isNaN(fFlow)) flowM3h = fFlow;
    } else if (category === 'heaters' || category === 'heat-pump') {
        powerkW = getHeaterPower({ name, description, raw: offer });
        if (category === 'heat-pump') {
            recommendedPoolVolume = extractRecommendedPoolVolume(name, description, offer);
        }
    }

    // Дополнительные Generic проверки
    if (powerkW === 0) {
        powerkW = extractGenericPower(name, description, offer);
    }

    const hasPrefilter = /префильтр/i.test(description) && !/без\s*префильтра/i.test(description);

    return {
        id, name, vendor, url, price, currency, category, description,
        image: picture, pictures, available,
        flowM3h, headM, powerkW, voltage, recommendedPoolVolume, hasPrefilter,
        raw: offer
    };
}

function determineCategory(name, description = '', offer = null, isRegularPump = false) {
    const text = `${name} ${description}`.toLowerCase();

    // Тепловые насосы
    const hasHeatPumpKeywords = /тепловой\s*насос|тепловые\s*насосы|heat\s*pump|heatpump/i.test(text);
    const hasVolume = extractRecommendedPoolVolume(name, description, offer);

    if (hasHeatPumpKeywords && hasVolume && !isRegularPump && !/теплообмен|heat\s*exchanger/i.test(name)) {
        return 'heat-pump';
    }


    if (/нагрев|теплообмен|[HН][EЕ]\s*[-]?\d+/i.test(name)) return 'heaters';
    if (isHeatPumpItem({ name, description })) return 'heat-pump';

    if (/теплообмен/i.test(text)) return 'heaters';


    if (isValidPumpItem({ name })) return 'pump';
    if (/хлоринатор|chlorinator|электролиз/i.test(name)) return 'disinfection';
    if (isUltravioletItem({ name })) return 'disinfection';


    if (/фильтр/i.test(text)) return 'filters';
    // ПВХ до «песок»: в названии плёнки часто «черный песок» (текстура), иначе category=sand
    if (/пвх|пленк|алькорплан|лайнер/i.test(text)) return 'pvc-film';
    if (/песок/i.test(text)) return 'sand';
    if (/лампа|прожектор|свет|light|led|светильник|подсветка/i.test(text)) return 'lighting';
    if (/лестниц/i.test(text)) return 'ladders';
    if (/мозаик/i.test(text)) return 'mosaic';
    if (/слив|дренаж/i.test(text)) return 'drains';
    if (/форсунк/i.test(text)) return 'nozzles';
    if (/скиммер/i.test(text)) return 'skimmers';

    return 'other';
}


function extractVoltage(name, description, offer) {
    const text = `${name} ${description}`.toLowerCase();
    if (/380\s*в|380v|3\s*фаз/i.test(text)) return '380В (3 фазы)';
    if (/220\s*в|220v|1\s*фаз/i.test(text)) return '220В (1 фаза)';

    const params = toArray(offer?.param).map(stringifyParam).join(' ').toLowerCase();
    if (/380\s*в|380v/i.test(params)) return '380В (3 фазы)';
    if (/220\s*в|220v/i.test(params)) return '220В (1 фаза)';

    return '220В';
}

function extractHead(name, description, offer) {
    const text = `${name} ${description}`.toLowerCase();
    const head = text.match(/(\d+(?:[.,]\d+)?)\s?(?:м\s*(?:вод\.?\s*столба|в\.?с\.?|напора))/i) ||
        text.match(/напор[:\s]+(\d+(?:[.,]\d+)?)/i);
    if (head) return toNumber(head[1]);

    const params = toArray(offer?.param).map(stringifyParam).join(' ').toLowerCase();
    const p = params.match(/(\d+(?:[.,]\d+)?)\s?(?:м\s*(?:вод\.?\s*столба|в\.?с\.?|напора))/i) ||
        params.match(/напор[:\s]+(\d+(?:[.,]\d+)?)/i);
    return p ? toNumber(p[1]) : 0;
}

function extractGenericPower(name, description, offer) {
    const item = { name, description, raw: offer };
    return getHeaterPower(item);
}

function toArray(v) { return Array.isArray(v) ? v : (v || v === 0) ? [v] : []; }
function safeStr(v) { return (v == null ? '' : String(v)).trim(); }
function toNumber(v) {
    if (v == null || v === '') return 0;
    const n = Number(String(v).replace(',', '.'));
    return Number.isFinite(n) ? n : 0;
}
function stringifyParam(p) {
    if (!p) return '';
    if (typeof p === 'string') return p;
    const name = p['@_name'] ? String(p['@_name']) : '';
    const val = p['#text'] ? String(p['#text']) : '';
    return `${name ? name + ': ' : ''}${val}`;
}
function mergeDescription(offer) {
    const parts = [];
    if (offer.description) parts.push(String(offer.description));
    toArray(offer.param).forEach(p => parts.push(stringifyParam(p)));
    if (offer.name) parts.push(String(offer.name));
    if (offer.model) parts.push(String(offer.model));
    if (offer.vendor) parts.push(String(offer.vendor));
    return parts.join(' • ');
}

function getPumpCapacityFromName(name = '', description = '', offer = null) {
    const text = `${name} ${description}`.toLowerCase();
    const m3h = text.match(/(\d+(?:[.,]\d+)?)\s?(?:м3\/ч|м³\/ч|м\^3\/ч)/i);
    if (m3h) return parseFloat(m3h[1].replace(',', '.'));
    const lmin = text.match(/(\d+(?:[.,]\d+)?)\s?(?:л\/мин|л\/м|l\/min)/i);
    if (lmin) return parseFloat((parseFloat(lmin[1].replace(',', '.')) * 0.06).toFixed(2));
    if (offer?.param) {
        const params = Array.isArray(offer.param) ? offer.param : [offer.param];
        const joined = params.map(p => {
            const n = p['@_name'] || '';
            const v = p['#text'] || '';
            return `${n}: ${v}`;
        }).join(' • ').toLowerCase();
        const p_m3h = joined.match(/(\d+(?:[.,]\d+)?)\s?(?:м3\/ч|м³\/ч|м\^3\/ч)/i);
        if (p_m3h) return parseFloat(p_m3h[1].replace(',', '.'));
        const p_lmin = joined.match(/(\d+(?:[.,]\d+)?)\s?(?:л\/мин|л\/м)/i);
        if (p_lmin) return parseFloat((parseFloat(p_lmin[1].replace(',', '.')) * 0.06).toFixed(2));
    }
    const m = String(name).match(/(?:производительность\s*)?(\d+(?:[.,]\d+)?)\s*(?:м3\/ч|м³\/ч|куб\.м\/ч)/i);
    if (m) return parseFloat(m[1].replace(',', '.'));
    return 0;
}

function isValidPumpItem(item) {
    if (!item || !item.name) return false;
    const name = item.name.toLowerCase();
    const bad = ['песочный', 'фильтровальная установка', 'моноблок', 'fsf', 'установка', 'комплект'];
    if (bad.some(b => name.includes(b))) return false;
    return name.includes('насос');
}

const FILTER_PARSE_PATTERNS = {
    FLOW_M3H: [
        /(\d+(?:[.,]\d+)?)\s*м3\/ч/i,
        /(\d+(?:[.,]\d+)?)\s*м³\/ч/i,
        /производительность\s+(\d+(?:[.,]\d+)?)/i
    ],
    FLOW_LPM: /(\d+(?:[.,]\d+)?)\s*л\/мин/i,
    FLOW_LPS: /(\d+(?:[.,]\d+)?)\s*л\/с/i,
    DIAMETER_MM: [
        /[dø](\d+)/i,
        /(\d+)\s*мм/i
    ],
    SAND_LOAD_KG: [
        /загрузка\s*песка[:\s]*(\d+)/i,
        /(\d+)\s*кг/i
    ]
};

function parseFilterFlow(product) {
    if (!product || !product.name) return NaN;
    const name = product.name.toLowerCase();

    for (const pattern of FILTER_PARSE_PATTERNS.FLOW_M3H) {
        const match = name.match(pattern);
        if (match) return parseFloat(match[1].replace(',', '.'));
    }

    const lpmMatch = name.match(FILTER_PARSE_PATTERNS.FLOW_LPM);
    if (lpmMatch) return (parseFloat(lpmMatch[1].replace(',', '.')) * 60) / 1000;

    const lpsMatch = name.match(FILTER_PARSE_PATTERNS.FLOW_LPS);
    if (lpsMatch) return parseFloat(lpsMatch[1].replace(',', '.')) * 3.6;

    return NaN;
}
